import { useContext, useEffect, useRef, useState } from 'react';
import { drawPolicyNewsWidget, parsePolicyNewsArticles, NAVER_NEWS_SOURCE_LABEL, POLICY_NEWS_SOURCE_LABEL, type NoticeDataWidgetLayer, type NoticeWidgetData } from '@cornerstone/shared';
import { API_BASE } from '../client/api';
import { contentGroupHeaders } from '../client/groupScope';
import { cachedWeatherPreview, WeatherPreviewContext } from './WeatherWidgetPreview';

/** Gallery/placeholder sample: shows the list layout before the server returns real 정책브리핑 data. */
export const POLICY_NEWS_SAMPLE: NoticeWidgetData = {
  status: 'ready', title: '정책브리핑 정책뉴스', sourceLabel: POLICY_NEWS_SOURCE_LABEL, updatedAt: '2026-09-27T12:00:00.000Z',
  lines: [], articles: [
    { org: '국토교통부', publishedAt: '2026-09-27T09:05:00.000Z', title: '정책브리핑 정책뉴스 제목이 이 자리에 두 줄까지 표시됩니다' },
    { org: '재정경제부', publishedAt: '2026-09-27T08:52:00.000Z', title: '부처별 최신 기사를 한 건씩 골라 보여줍니다' },
    { org: '소방청', publishedAt: '2026-09-27T08:38:00.000Z', title: '같은 주제의 기사가 이어지지 않도록 걸러냅니다' },
    { org: '산업통상부', publishedAt: '2026-09-27T08:05:00.000Z', title: '공공누리 제1유형 정책뉴스만 표시합니다' },
    { org: '기후에너지환경부', publishedAt: '2026-09-27T08:00:00.000Z', title: '서버가 30분마다 새 소식을 받아옵니다' },
    { org: '보건복지부', publishedAt: '2026-09-27T07:40:00.000Z', title: '출처와 기준 시각은 목록 아래에 함께 표시됩니다' },
  ],
};

/** 네이버 뉴스 위젯 샘플: 언론사 도메인 · 발행 시각 · 제목, 출처 "네이버 검색결과". */
export const NAVER_NEWS_SAMPLE: NoticeWidgetData = {
  status: 'ready', title: '네이버 뉴스', sourceLabel: NAVER_NEWS_SOURCE_LABEL, updatedAt: '2026-09-27T12:00:00.000Z',
  lines: [], articles: [
    { org: 'news.example.co.kr', publishedAt: '2026-09-27T09:10:00.000Z', title: '검색어로 찾은 최신 뉴스 제목이 이 자리에 두 줄까지 표시됩니다' },
    { org: 'daily.example.com', publishedAt: '2026-09-27T08:55:00.000Z', title: '네이버가 준 순서 그대로 보여줍니다' },
    { org: 'press.example.kr', publishedAt: '2026-09-27T08:40:00.000Z', title: '언론사 주소와 발행 시각을 함께 표시합니다' },
    { org: 'times.example.com', publishedAt: '2026-09-27T08:20:00.000Z', title: '서버가 10분마다 새 소식을 받아옵니다' },
    { org: 'biz.example.co.kr', publishedAt: '2026-09-27T08:02:00.000Z', title: '운영사가 등록한 공용 키로 동작합니다' },
    { org: 'local.example.kr', publishedAt: '2026-09-27T07:45:00.000Z', title: '출처는 목록 아래에 네이버 검색결과로 표시됩니다' },
  ],
};

export const newsListSample = (layer: Pick<NoticeDataWidgetLayer, 'source'>): NoticeWidgetData =>
  layer.source.kind === 'naverNews' ? NAVER_NEWS_SAMPLE : POLICY_NEWS_SAMPLE;

export function PolicyNewsWidgetCanvas({ layer, data }: { layer: NoticeDataWidgetLayer; data: NoticeWidgetData | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const node = canvas.current, ctx = node?.getContext('2d');
    if (!node || !ctx) return;
    const scale = Math.min(1, 2048 / Math.max(layer.width, layer.height));
    node.width = Math.max(1, Math.round(layer.width * scale)); node.height = Math.max(1, Math.round(layer.height * scale));
    ctx.scale(scale, scale); drawPolicyNewsWidget(ctx, { ...layer, x: 0, y: 0 }, data);
  }, [layer, data]);
  return <canvas ref={canvas} role="img" aria-label={`${layer.source.kind === 'naverNews' ? '네이버 뉴스' : '정책브리핑 정책뉴스'} 미리보기 · ${(data?.articles ?? []).length}건`} style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} />;
}

const sourceKey = (layer: NoticeDataWidgetLayer) => JSON.stringify([API_BASE, contentGroupHeaders(), layer.source]);
/** Editor preview with the real server list (same cache as the thumbnail renderer); sample until it arrives. */
export function PolicyNewsWidgetPreview({ layer }: { layer: NoticeDataWidgetLayer }) {
  const cache = useContext(WeatherPreviewContext), source = sourceKey(layer);
  const [data, setData] = useState<NoticeWidgetData | null>(() => cachedWeatherPreview(cache, layer));
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const body: NoticeDataWidgetLayer = { id: layer.id, kind: 'dataWidget', source: layer.source, title: '뉴스 목록',
      x: 0, y: 0, width: 600, height: 600, z: 0, opacity: 1, rotation: 0, fontSize: 24, color: '#000000', backgroundColor: null };
    const update = (next: NoticeWidgetData) => { if (!active) return; cache?.set(layer.id, { source, data: next }); setData(next); };
    fetch(`${API_BASE}/api/notices/widget-preview`, { method: 'POST', credentials: 'include', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...contentGroupHeaders() }, body: JSON.stringify(body) })
      .then(async (response) => {
        if (!response.ok) throw new Error('preview failed');
        const result = await response.json() as NoticeWidgetData;
        update({ ...result, articles: parsePolicyNewsArticles(result.articles) });
      })
      .catch(() => update({ ...newsListSample(layer), message: '미리보기 샘플입니다. 기기에서는 실제 뉴스가 표시됩니다.' }))
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [source, layer.id, cache]);
  return <PolicyNewsWidgetCanvas layer={layer} data={data ?? newsListSample(layer)} />;
}
